import { useEffect, useRef } from 'react';

/**
 * Auditoria 4 - Item 19: hook simple para auto-refrescar datos en paginas de ops.
 * - Ejecuta fetchFn cada intervalMs (default 30s).
 * - Solo corre cuando el documento esta visible (pausa con pestana en background).
 * - Refresca al volver a foco/visibilidad.
 *
 * Uso: useAutoRefresh(fetchData, 30000);
 */
export function useAutoRefresh(fetchFn, intervalMs = 30000, enabled = true) {
    const savedFn = useRef(fetchFn);

    useEffect(() => { savedFn.current = fetchFn; }, [fetchFn]);

    useEffect(() => {
        if (!enabled || !intervalMs) return undefined;
        let timerId = null;

        const tick = () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            try { savedFn.current && savedFn.current(); } catch (_e) { /* noop */ }
        };

        const start = () => {
            if (timerId) return;
            timerId = setInterval(tick, intervalMs);
        };
        const stop = () => {
            if (timerId) { clearInterval(timerId); timerId = null; }
        };

        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                tick();
                start();
            } else {
                stop();
            }
        };

        start();
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', tick);

        return () => {
            stop();
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', tick);
        };
    }, [intervalMs, enabled]);
}

export default useAutoRefresh;
